import { useEffect, useState } from 'react';
import { ApiFailure, api } from '../lib/api';

interface Profile {
  code: string;
  name: string;
  timezone: string;
  currency: string;
  addressLocale: string;
  prefix: string;
  orderCount: number;
}

const INPUT =
  'mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-800';

/** 常见时区 —— 直接选,别让人去猜 IANA 的写法 */
const ZONES = [
  'Pacific/Auckland',
  'Australia/Sydney',
  'Asia/Shanghai',
  'Asia/Kuala_Lumpur',
  'Asia/Singapore',
  'Asia/Tokyo',
  'UTC',
];

const LOCALES: Array<{ value: string; label: string }> = [
  { value: 'en-NZ', label: '新西兰(+64)' },
  { value: 'en-AU', label: '澳大利亚(+61)' },
  { value: 'en-MY', label: '马来西亚(+60)' },
  { value: 'en-SG', label: '新加坡(+65)' },
  { value: 'zh-CN', label: '中国大陆(+86)' },
  { value: 'generic', label: '不指定' },
];

/**
 * 租户信息。
 *
 * ★ 这一屏在这之前**根本不存在**:租户是 seed 脚本建出来的,
 *   名字、时区、货币、单号前缀全写在代码里,要改只能连数据库。
 *   而这几个值都直接决定业务:
 *
 *   · **时区** 决定「今天」从几点开始 —— 排班、日报、单号里的日期全看它
 *   · **地区** 决定电话号码怎么补区号(司机输 0211234567,存的是 +64211234567)
 *   · **货币** 决定所有金额显示成什么
 *   · **单号前缀** 是客户和客服在电话里念的那几个字母
 *
 *   一个交付给客户的系统,这几样不能只能靠改代码。
 */
export function TenantSettings({ canManage }: { canManage: boolean }) {
  const [p, setP] = useState<Profile | null>(null);
  const [f, setF] = useState<Partial<Profile>>({});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    void api<Profile>('/settings/tenant')
      .then((r) => {
        setP(r);
        setF(r);
      })
      .catch((e) => setErr(e instanceof ApiFailure ? e.message : '读取失败'));
  }, []);

  async function save() {
    setBusy(true);
    setErr(null);
    setSaved(false);
    try {
      await api('/settings/tenant', {
        method: 'PATCH',
        body: {
          name: f.name,
          timezone: f.timezone,
          currency: f.currency,
          addressLocale: f.addressLocale,
          prefix: f.prefix,
        },
      });
      setSaved(true);
      const fresh = await api<Profile>('/settings/tenant');
      setP(fresh);
      setF(fresh);
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '保存失败');
    } finally {
      setBusy(false);
    }
  }

  if (!p) {
    return (
      <section aria-label="租户信息">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">租户信息</h2>
        <p className="mt-2 text-sm text-slate-400">{err ?? '加载中…'}</p>
      </section>
    );
  }

  return (
    <section aria-label="租户信息">
      <h2 className="mb-1 text-xl font-semibold text-slate-900 dark:text-slate-100">租户信息</h2>
      <p className="mb-4 text-sm text-slate-500">
        代号 <code>{p.code}</code> —— 代号定下来就不改了,它出现在日志、审计和对接方那边
      </p>

      <div className="max-w-xl space-y-3 rounded-xl border border-slate-200 p-4 dark:border-slate-700">
        <label className="block text-sm">
          <span className="text-slate-500">名称</span>
          <span className="block text-xs text-slate-400">显示在左上角和司机端</span>
          <input
            aria-label="租户名称"
            value={f.name ?? ''}
            disabled={!canManage}
            onChange={(e) => setF({ ...f, name: e.target.value })}
            className={INPUT}
          />
        </label>

        <label className="block text-sm">
          <span className="text-slate-500">时区</span>
          {/*
            ★ 这一栏影响最大,而且错了不会报错。
              「今天」从几点开始由它决定 —— 排班、日报、单号里的日期全看它。
              填成邻国的时区,每天午夜前后两小时的单会落到「昨天」。
          */}
          <span className="block text-xs text-slate-400">
            决定「今天」从几点开始:排班、日报、单号里的日期都按它算
          </span>
          <select
            aria-label="时区"
            value={f.timezone ?? ''}
            disabled={!canManage}
            onChange={(e) => setF({ ...f, timezone: e.target.value })}
            className={INPUT}
          >
            {[...new Set([p.timezone, ...ZONES])].map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
        </label>

        {/*
          ★ 两栏并排时,输入框要**对齐在同一条线上**。
            两边的说明文字长度不一样(「三个字母,比如 NZD」一行,
            「客服在电话里念的那几个字母…」折成两行),而 label 各自按
            自己的内容排版 —— 于是右边的输入框比左边低一截,看着像排版坏了。
            用 flex 让说明区撑开、输入框落到底部对齐。
        */}
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex h-full flex-col text-sm">
            <span className="text-slate-500">货币</span>
            <span className="flex-1 text-xs text-slate-400">三个字母,比如 NZD</span>
            <input
              aria-label="货币"
              value={f.currency ?? ''}
              disabled={!canManage}
              maxLength={3}
              onChange={(e) => setF({ ...f, currency: e.target.value.toUpperCase() })}
              className={`${INPUT} font-mono`}
            />
          </label>

          <label className="flex h-full flex-col text-sm">
            <span className="text-slate-500">单号前缀</span>
            <span className="flex-1 text-xs text-slate-400">
              客服在电话里念的那几个字母;已经建出来的单不受影响
            </span>
            <input
              aria-label="单号前缀"
              value={f.prefix ?? ''}
              disabled={!canManage}
              maxLength={4}
              onChange={(e) => setF({ ...f, prefix: e.target.value.toUpperCase() })}
              className={`${INPUT} font-mono`}
            />
          </label>
        </div>

        <label className="block text-sm">
          <span className="text-slate-500">所在地区</span>
          {/* ★ 它决定电话怎么补区号:司机输 0211234567,存进去的是 +64211234567 */}
          <span className="block text-xs text-slate-400">
            决定手机号自动补哪个国家的区号,以及地址怎么规整
          </span>
          <select
            aria-label="所在地区"
            value={f.addressLocale ?? 'generic'}
            disabled={!canManage}
            onChange={(e) => setF({ ...f, addressLocale: e.target.value })}
            className={INPUT}
          >
            {LOCALES.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
        </label>

        {/*
          ★ 已经有单之后改这几样,要说清楚影响的是**以后**的单。
            不说的话,人会以为改完历史单也跟着变(而那才是灾难)。
        */}
        {p.orderCount > 0 && (
          <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800
                          dark:bg-amber-950 dark:text-amber-300">
            库里已经有 {p.orderCount} 单。改时区和前缀只影响**以后**建的单;
            已经发出去的单号和已经算好的日期不会跟着变
          </div>
        )}

        {err && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}

        {canManage && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => void save()}
              disabled={busy}
              className="rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white
                         disabled:opacity-40"
            >
              {busy ? '保存中…' : '保存'}
            </button>
            {saved && <span className="text-sm text-green-600 dark:text-green-400">已保存</span>}
          </div>
        )}
      </div>
    </section>
  );
}
